import { useEffect, useState } from 'react';
import { applyStatusBarTheme } from '../lib/native';

export type Theme = 'light' | 'dark';

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');

  // Keep the browser/PWA chrome and the native status bar in sync.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'dark' ? '#1C1B19' : '#F4ECD2');
  void applyStatusBarTheme(theme);
}

export function getCurrentTheme(): Theme {
  const saved = localStorage.getItem('theme') as Theme | null;
  if (saved) return saved;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(getCurrentTheme);

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  function setTheme(t: Theme) { setThemeState(t); }
  function toggle() { setThemeState((t) => (t === 'dark' ? 'light' : 'dark')); }

  return { theme, setTheme, toggle };
}

/**
 * useTheme() above is local component state — it only reflects a toggle
 * fired from that same component, not one fired elsewhere (e.g. a parent
 * layout's header button), even in a component that does re-render off the
 * back of that toggle: the DOM class flip happens in a `useEffect`, which
 * runs after render, so a plain read during render is one cycle stale.
 * Use this instead wherever you need the theme reactively and don't own
 * the toggle control yourself — it watches the actual source of truth
 * (the `dark` class on <html>) via MutationObserver, so it's correct
 * regardless of which component changed it.
 */
export function useIsDarkTheme(): boolean {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));

  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => setIsDark(root.classList.contains('dark')));
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

// Apply immediately on page load (before React mounts) to avoid a flash.
applyTheme(getCurrentTheme());
